/*
 * Sign-in workflow for a visitor who wants to continue reading the current public page.
 * Used by the public-login link in application navigation.
 * Retains the destination and scroll position even if the initiating link is removed.
 * Example: const login = context.service(createPublicLoginWorkflow);
 * Use capturePublicPageScroll(root) when recording the public page position.
 */

import { isAuthCancelled } from '../../runtime/auth.js';
import { servicesContext } from '../../runtime/services.js';

/** Capture semantic scroll containers, including the continuum cover without an ID. */
export function capturePublicPageScroll(root) {
  const positions = [],
    seen = new Set();

  const remember = (node, find) => {
    if (!node || seen.has(node)) return;
    seen.add(node);
    if (node.scrollTop || node.scrollLeft)
      positions.push({ find, top: node.scrollTop, left: node.scrollLeft });
  };

  remember(root, (replacement) => replacement);
  // A stable view key takes precedence over generated accessibility IDs.
  for (const attr of ['data-scroll-key', 'id']) {
    for (const node of root?.querySelectorAll(`[${attr}]`) || []) {
      const key = node.getAttribute(attr);
      remember(node, (replacement) =>
        [...replacement.querySelectorAll(`[${attr}]`)].find(
          (item) => item.getAttribute(attr) === key
        )
      );
    }
  }
  for (const selector of ['.view-cover', '.chat-log', '.chapter-log']) {
    [...(root?.querySelectorAll(selector) || [])].forEach((node, index) => {
      remember(node, (replacement) => replacement.querySelectorAll(selector)[index]);
    });
  }

  return (replacement) => {
    if (!replacement) return;
    for (const { find, top, left } of positions) {
      const node = find(replacement);
      if (node) {
        node.scrollTop = top;
        node.scrollLeft = left;
      }
    }
  };
}

/** The pending sign-in belongs to the document, including navigation replacement. */
export function createPublicLoginWorkflow(context) {
  context = servicesContext(context, ['auth']);
  const auth = context.services.auth;
  const browser = context.window;
  let pending = null;
  return {
    signIn({ refresh, onError }) {
      if (context.signal.aborted || pending || auth.getUser()) return pending;
      const owner = auth.getAuthOwner(),
        url = browser.location.href;
      const restoreScroll = capturePublicPageScroll(
        context.document.getElementById('main-content')
      );
      pending = (async () => {
        const identity = await auth.requireAuthentication({ owner, intent: 'public-login' });
        if (
          context.signal.aborted ||
          owner !== auth.getAuthOwner() ||
          identity !== auth.getAuthContext() ||
          browser.location.href !== url
        )
          return;
        await refresh({ owner, context: identity, url, restoreScroll });
      })()
        .catch((error) => {
          if (
            !context.signal.aborted &&
            !isAuthCancelled(error) &&
            owner === auth.getAuthOwner() &&
            browser.location.href === url
          )
            return onError?.(error);
        })
        .finally(() => {
          pending = null;
        });
      return pending;
    }
  };
}
